import {
  FormSectionCard,
  GradientText,
  SubmitButton,
  SubmitButtonContainer,
} from '../PasserCommande/PasserCommande.styles'
import React, { useRef, useState, useMemo, useEffect, useCallback, lazy, Suspense } from 'react'
import CommonHeader from '../../Components/CommonHeader/common-header.component'
import { CustomInput } from './style'
import Footer from '../../Components/Footer/footer.component'
import { signup } from '../../api/auth'
import toast from 'react-hot-toast'
import { useForm, Controller } from 'react-hook-form'
import { useHistory } from 'react-router'
import { validatePhoneNumber } from '../../utils/phoneValidator'
import { Link } from 'react-router-dom'
import countries from 'i18n-iso-countries'
import fr from 'i18n-iso-countries/langs/fr.json'
import en from 'i18n-iso-countries/langs/en.json'
import './RegisterTransporter.css'
import { useTranslation } from 'react-i18next'
import Modal from 'react-modal'
import PhoneInput from 'react-phone-input-2' // Import normal
import 'react-phone-input-2/lib/style.css' // Import du style

// Lazy loading uniquement pour Select
const Select = lazy(() => import('react-select'))

countries.registerLocale(fr)
countries.registerLocale(en)
Modal.setAppElement('#root')

// Hook personnalisé pour détecter mobile
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768)

  useEffect(() => {
    const checkIfMobile = () => setIsMobile(window.innerWidth <= 768)
    const debouncedCheck = debounce(checkIfMobile, 150)
    
    window.addEventListener('resize', debouncedCheck)
    return () => window.removeEventListener('resize', debouncedCheck)
  }, [])

  return isMobile
}

// Fonction debounce simple
const debounce = (func, wait) => {
  let timeout
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout)
      func(...args)
    }
    clearTimeout(timeout)
    timeout = setTimeout(later, wait)
  }
}

// Composant de chargement simple
const LoadingSpinner = () => (
  <div style={{ 
    padding: '10px', 
    textAlign: 'center',
    color: '#666' 
  }}>
    Chargement...
  </div>
)

function RegisterTransporter() {
  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: {
      vehicules: [],
      manutention: [],
      epi: [],
      rippeurs: 0,
    }
  })

  const password = useRef({})
  password.current = watch('password', '')
  const { t, i18n } = useTranslation()
  const history = useHistory()
  const isMobile = useIsMobile()

  const [loading, setLoading] = useState(false)
  const [modalIsOpen, setModalIsOpen] = useState(false)

  // Mémorisation des options de pays
  const countryOptions = useMemo(() => {
    const countryNames = countries.getNames(i18n.language, { select: 'official' })
    return Object.entries(countryNames)
      .map(([code, name]) => ({
        value: code,
        label: name,
      }))
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [i18n.language])

  // Fonction pour traiter les fichiers
  const processFiles = useCallback((data) => {
    const files = []
    const fileFields = [
      'fiscale', 'urssaf', 'assurance_marchandises', 'kbis',
      'licence_moins35t', 'licence_plus35t', 'capacite_transport',
      'piece_identite', 'rib', 'logo'
    ]

    fileFields.forEach((field) => {
      if (data[field]) {
        files.push({ [field]: data[field] })
        delete data[field]
      }
    })

    if (Array.isArray(data.vehicules)) {
      data.vehicules.forEach((vehicule, index) => {
        const { immatriculation, carte_grise } = vehicule
        if (carte_grise) {
          files.push({
            [`carte_grise_${index}_${immatriculation}`]: carte_grise
          })
          delete vehicule.carte_grise
        }
      })
    }

    return files
  }, [])

  // Fonction pour gérer les erreurs
  const handleError = useCallback((error) => {
    const data = error?.response?.data?.errors
    if (data?.msg === 'EMAIL_ALREADY_EXISTS') {
      toast.error(
        'Ce mail est déjà utilisé par un autre utilisateur.',
        { style: { fontFamily: 'Montserrat' } }
      )
    } else {
      toast.error(
        error.response?.data.error ||
        error.response?.data.message ||
        "Une erreur s'est produite",
        { style: { fontFamily: 'Montserrat' } }
      )
    }
  }, [])

  // Optimisation de la fonction onSubmit
  const onSubmit = useCallback(async (data) => {
    if (data.password !== data.confirm_password) {
      toast.error('Veuillez vérifier votre mot de passe', {
        style: { fontFamily: 'Montserrat' }
      })
      return
    }

    // Nettoyer les données
    const cleanedData = { ...data }
    delete cleanedData.confirm_password
    delete cleanedData.acceptTerms

    // Initialiser les valeurs par défaut
    if (data.pays?.value === 'FR') {
      cleanedData.tva = true
    }

    cleanedData.vehicules = Array.isArray(data.vehicules) ? data.vehicules : []
    cleanedData.rippeurs = parseInt(data.rippeurs) || 0
    cleanedData.manutention = Array.isArray(data.manutention) ? data.manutention : []
    cleanedData.epi = Array.isArray(data.epi) ? data.epi : []

    // Traiter les fichiers
    const files = processFiles(cleanedData)
    delete cleanedData.logo

    setLoading(true)
    try {
      await signup({ ...cleanedData, role: 'transporter' }, files)
      setModalIsOpen(true)
    } catch (error) {
      handleError(error)
    } finally {
      setLoading(false)
    }
  }, [processFiles, handleError])

  // Callback pour fermer le modal
  const handleCloseModal = useCallback(() => {
    setModalIsOpen(false)
    history.push('/')
  }, [history])

  return (
    <div>
      <CommonHeader />
      <div className='transporter-form-container'>
        <GradientText style={{ fontSize: isMobile ? '1.5rem' : '2.5rem', textAlign: 'center' }}>
          {t("create_transporter_account_button").toUpperCase()}
        </GradientText>
        <div className='transporter-form-wrapper'>
          <form onSubmit={handleSubmit(onSubmit)}>
            <FormSectionCard>
              <div className='form-row'>
                <div className='form-group'>
                  <label htmlFor='societe'>
                    {t("company_name")} <span className='required'>*</span>
                  </label>
                  <CustomInput
                    type='text'
                    id='societe'
                    {...register('societe', {
                      required: t("err_message1") + t("company_name"),
                      minLength: {
                        value: 3,
                        message: t("article_m") + t("company_name") + t("err_message2"),
                      },
                      validate: (value) =>
                        /[A-Za-z0-9]/.test(value) ||
                        t("article_m") + t("company_name_err_message"),
                    })}
                  />
                  {errors.societe && (
                    <div className='error-message'>
                      {errors.societe.message}
                    </div>
                  )}
                </div>
                <div className='form-group'>
                  <label htmlFor='last_name'>
                    {t("last_name")} <span className='required'>*</span>
                  </label>
                  <CustomInput
                    type='text'
                    id='last_name'
                    {...register('last_name', {
                      required: t("err_message1") + t("last_name"),
                      minLength: {
                        value: 3,
                        message: t("article_m") + t("last_name") + t("err_message2"),
                      },
                      pattern: {
                        value: /^[A-Za-zÀ-ÖØ-öø-ÿ\s'\-]+$/,
                        message: t("article_m") + t("last_name") + t("err_message3"),
                      },
                    })}
                  />
                  {errors.last_name && (
                    <div className='error-message'>
                      {errors.last_name.message}
                    </div>
                  )}
                </div>
              </div>

              <div className='form-row'>
                <div className='form-group'>
                  <label htmlFor='first_name'>
                    {t("first_name")} <span className='required'>*</span>
                  </label>
                  <CustomInput
                    type='text'
                    id='first_name'
                    {...register('first_name', {
                      required: t("err_message1") + t("first_name"),
                      minLength: {
                        value: 3,
                        message: t("article_m") + t("first_name") + t("err_message2"),
                      },
                      pattern: {
                        value: /^[A-Za-zÀ-ÖØ-öø-ÿ\s'\-]+$/,
                        message: t("article_m") + t("first_name") + t("err_message3"),
                      },
                    })}
                  />
                  {errors.first_name && (
                    <div className='error-message'>
                      {errors.first_name.message}
                    </div>
                  )}
                </div>
                <div className='form-group'>
                  <label htmlFor='quality'>
                    {t("quality")} <span className='required'>*</span>
                  </label>
                  <CustomInput
                    type='text'
                    id='quality'
                    {...register('quality', {
                      required: t("err_message1") + t("quality"),
                      minLength: {
                        value: 3,
                        message: t("article_f") + t("quality") + t("err_message2"),
                      },
                      pattern: {
                        value: /^[A-Za-zÀ-ÖØ-öø-ÿ\s'\-]+$/,
                        message: t("article_f") + t("quality") + t("err_message3"),
                      },
                    })}
                  />
                  {errors.quality && (
                    <div className='error-message'>
                      {errors.quality.message}
                    </div>
                  )}
                </div>
              </div>

              <div className='form-group'>
                <label htmlFor='pays'>
                  {t("country")} <span className='required'>*</span>
                </label>
                <Controller
                  name='pays'
                  control={control}
                  rules={{ required: t("country_error_message") }}
                  render={({ field: { onChange, value } }) => (
                    <Suspense fallback={<LoadingSpinner />}>
                      <Select
                        placeholder={t("country_placeholder")}
                        options={countryOptions}
                        value={countryOptions.find(
                          (opt) => opt.value === value?.value
                        )}
                        onChange={onChange}
                        classNamePrefix='select'
                      />
                    </Suspense>
                  )}
                />
                {errors.pays && (
                  <div className='error-message'>
                    {errors.pays.message}
                  </div>
                )}
              </div>

              <div className='form-row'>
                <div className='form-group'>
                  <label htmlFor='city'>
                    {t("city")} <span className='required'>*</span>
                  </label>
                  <CustomInput
                    type='text'
                    id='city'
                    {...register('city', {
                      required: t("err_message1") + t("city"),
                      minLength: {
                        value: 3,
                        message: t("article_f") + t("city") + t("err_message2"),
                      },
                      pattern: {
                        value: /^.+$/,
                        message: t("article_f") + t("ville") + t("err_message3"),
                      },
                    })}
                  />
                  {errors.city && (
                    <div className='error-message'>
                      {errors.city.message}
                    </div>
                  )}
                </div>
              </div>

              <div className='form-row'>
                <div className='form-group'>
                  <label htmlFor='email'>
                    {t("email")} <span className='required'>*</span>
                  </label>
                  <CustomInput
                    type='text'
                    id='email'
                    {...register('email', {
                      required: t("err_message1") + t("email"),
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
                        message: t("email_err_message"),
                      },
                    })}
                  />
                  {errors.email && (
                    <div className='error-message'>
                      {errors.email.message}
                    </div>
                  )}
                </div>
                <div className='form-group'>
                  <label htmlFor='phone'>
                    {t("contact_us_formulair_item2")} <span className='required'>*</span>
                  </label>
                  <Controller
                    name='phone'
                    control={control}
                    rules={{
                      required: t("err_message1") + t("contact_us_formulair_item2"),
                      validate: validatePhoneNumber,
                    }}
                    render={({ field }) => (
                      <PhoneInput
                        country={'fr'}
                        value={field.value}
                        onChange={field.onChange}
                        inputClass='phone-input'
                        buttonClass='phone-button'
                      />
                    )}
                  />
                  {errors.phone && (
                    <div className='error-message'>
                      {errors.phone.message}
                    </div>
                  )}
                </div>
              </div>
            </FormSectionCard>

            <div className='terms-container'>
              <input
                type='checkbox'
                id='acceptTerms'
                {...register('acceptTerms', {
                  required: t("terms_and_conditions_err_message"),
                })}
              />
              <label htmlFor='acceptTerms' className='terms-label'>
                {t("accept_terms_and_conditions")} {' '}
                <Link to='/cgv' target='_blank' className='terms-link'>
                  {t("terms_and_conditions")}
                </Link>
              </label>
            </div>

            {errors.acceptTerms && (
              <div className='error-message terms-error'>
                {errors.acceptTerms.message}
              </div>
            )}

            <div className='section-spacer' />

            <SubmitButtonContainer>
              <SubmitButton type='submit' disabled={loading}>
                {loading ? (
                  <div className='loader'></div>
                ) : (
                  t("sign_up")
                )}
              </SubmitButton>
            </SubmitButtonContainer>
          </form>
        </div>
      </div>
      <Footer />

      <Modal
        isOpen={modalIsOpen}
        onRequestClose={() => setModalIsOpen(false)}
        className='modal'
        overlayClassName='overlay'
        contentLabel='Félicitation'
      >
        <div className='modal-content'>
          <GradientText style={{ fontSize: isMobile ? '1.5rem' : '2.5rem' }}>
            {t("model_title").toUpperCase()}
          </GradientText>
          <div className='modal-message'>
            {t("model_msg1")}
            <br />
            {t("model_msg4")}
          </div>
          <button onClick={handleCloseModal} className='modal-button'>
            OK
          </button>
        </div>
      </Modal>
    </div>
  )
}

export default RegisterTransporter